<template>
  <div
      class="ui-input"
      v-bind:class="{ mini: mini, 'show-valid-indicator': showValidIndicator, info: hasInfo }"
      :disabled="disabled"
      :invalid="false"
      :valid="valid"
      :touched="touched"
      sklepalnik-ui-element
  >
    <input
        ref="$input"
        type="text"
        :name="name"
        :value="value"
        :maxlength="maxlength"
        :minlength="minlength"
        @input="onInput"
        @change="onChange"
        v-on:focus="onFocus"
        v-on:focusout="onFocusOut"
        v-bind:class="{ 'icon-padding': isIcon }"
        :disabled="disabled"
        :autocomplete="getCalculatedAutocomplete"
    />

    <span
        ref="$label"
        class="label"
        v-bind:class="{ up: isFocused || (value && value.length) }"
        @click="onLabelClicked()"
    >{{ label }} <span v-if="required">*</span>
    </span>

    <div class="valid-icon">
      <svg
          width="18"
          height="14"
          viewBox="0 0 18 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
      >
        <path
            d="M17 1L6 13L1 8"
            stroke="#1EA89C"
            stroke-linecap="round"
            stroke-linejoin="round"
        />
      </svg>
    </div>

    <div
        class="autocomplete-options"
        ref="autocomplete-options"
    >
      <div class="autocomplete-options__list" ref="autocomplete-options_list">
        <div
            class="options__list-option"
            v-for="o of autoCompleteOptions"
            :key="o.label"
            @click="onAutoCompleteOptionSelected(o)"
        >
          <div class="option__text">{{ o.label }}</div>
        </div>
      </div>

      <div class="google-footer">
        <img class="logo" src="@/assets/powered-by-google.png"/>
      </div>
    </div>

    <field-validator
        v-if="showValidator()"
        ref="validator"
        :required="required"
        :validate="validate"
        :fieldname="name"
        :limit="true"
        :value="value"
        :type="String"
        v-on:run-result="
        valid = $event;
        $emit('validated', valid);"
        :hide="mini"
    ></field-validator>
  </div>
</template>

<script>
import Vue from "vue";
import FieldValidator from "./FieldValidator.vue";
import {
  FieldValidatorProps,
  FieldValidatorAcknowledgeFG,
} from "./FieldValidatorUtils";

export default {
  name: "ui-input",
  components: {
    "field-validator": FieldValidator,
  },
  props: {
    label: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    value: {
      required: true,
    },
    mini: {
      type: Boolean,
      required: false,
    },
    hasInfo: {
      type: Boolean,
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
    },
    maxlength: {
      type: Number,
      required: false,
    },
    minlength: {
      type: Number,
      required: false,
    },
    showValidIndicator: {
      type: Boolean,
      required: false,
    },
    required: FieldValidatorProps.Required,
    validate: FieldValidatorProps.Validate,

    autocomplete: {
      type: Boolean,
      required: false,
      default: false
    },
    autoCompleteOptions: {
      type: Array,
      required: false
    }
  },
  data: function () {
    return {
      isFocused: false,
      isIcon: false,
      fg: undefined,
      valid: false,
      touched: false,

      autoCompleteShown: false
    };
  },
  computed: {
    getCalculatedAutocomplete() {
      switch (this.name) {
        case "customerName": {
          return "given-name";
        }

        case "customerSurname": {
          return "family-name";
        }

        case "customerStreet": {
          return "street-address";
        }

        case "customerHouse": {
          return "address-line2";
        }

        case "customerZipCode": {
          return "postal-code";
        }

        case "customerArea": {
          return "address-level2";
        }

        case "customerPhone": {
          return "tel";
        }

        case "customerEmail": {
          return "email";
        }
      }
      return null;
    }
  },
  methods: {
    onInput(event) {
      this.$emit("input", event.target.value);
      this.touched = true;

      if ((this.autocomplete && Array.isArray(this.autoCompleteOptions) && this.autoCompleteOptions.length > 0) && this.isFocused && !this.autoCompleteShown) {
        this.onShowAutocomplete();
      }

      // This needs to be checked as this method can be called programmatically with a fake event passed in, too
      if (!event.type && this.autoCompleteShown) {
        this.onHideAutocomplete();
      }

      Vue.nextTick(() => {
        if (this.hasValidator()) {
          this.$refs.validator.onFieldInput();
        }
      });
    },
    onChange(event) {
      this.$emit("change", event.target.value);
      this.$emit("fg-value-change");
    },
    onLabelClicked() {
      if (this.disabled) return;

      if (!this.isFocused) {
        this.isFocused = true;
        this.$refs["$input"].focus();
      }
    },
    showValidator() {
      return (this.validate || this.required) && this.fg;
    },
    hasValidator() {
      return this.required || this.validate;
    },
    runValidator() {
      if (this.hasValidator()) {
        return this.$refs.validator.run();
      }
    },
    isValid() {
      return this.valid;
    },
    onShowAutocomplete() {
      this.autoCompleteShown = true;
      const optionsRefs = this.$refs['autocomplete-options_list'].querySelectorAll('.options__list-option');

      let totalOptionsHeight = 20;

      for (let i = 0; i < optionsRefs.length; i++) {
        const optionRef = optionsRefs[i];

        totalOptionsHeight += optionRef.clientHeight + 0.5;

        if (i % 2 === 1) {
          optionRef.style.background = "rgb(242, 242, 242, 0.3)";
        }
      }

      const screenHeight =
          window.innerHeight ||
          document.documentElement.clientHeight ||
          document.body.clientHeight;

      const fieldBoundingClientRect = this.$refs.$input.getBoundingClientRect();
      if (fieldBoundingClientRect.bottom + totalOptionsHeight > screenHeight) {
        totalOptionsHeight = screenHeight - fieldBoundingClientRect.bottom - 10;
      }

      this.$refs['autocomplete-options'].style.visibility = 'visible';
      this.$refs['autocomplete-options'].style.height = totalOptionsHeight + 'px';
      this.$refs['autocomplete-options'].style.width = fieldBoundingClientRect.width + 'px';
      this.$refs.$input.style.borderRadius = '25px 25px 0 0';
    },
    onHideAutocomplete() {
      this.autoCompleteShown = false;

      this.$refs['autocomplete-options'].style.visibility = 'hidden';
      this.$refs['autocomplete-options'].style.height = '0';
      this.$refs.$input.style.borderRadius = '25px';
    },
    onAutoCompleteOptionSelected(selectedOption) {
      this.$emit('autocomplete-selected-option', selectedOption);
      this.onHideAutocomplete();
    },
    onFocus() {
      this.isFocused = true;
    },
    onFocusOut() {
      this.isFocused = false;

      setTimeout(() => {
        if (this.autocomplete && Array.isArray(this.autoCompleteOptions) && this.autoCompleteOptions.length > 0) this.onHideAutocomplete();
      }, 200);
    }
  },
  watch: {
    isFocused(newValue) {
      if (!newValue) this.runValidator();
    },
    value() {
      Vue.nextTick(() => {
        this.runValidator();
      });
    },
    required() {
      Vue.nextTick(() => {
        this.fg.onElementValidateChanged(this);
      });
    },
    validate() {
      Vue.nextTick(() => {
        this.fg.onElementValidateChanged(this);
      });
    },
    autoCompleteOptions(newValue) {
      if (Array.isArray(newValue) && newValue.length > 0) {
        Vue.nextTick(() => {
          this.onShowAutocomplete();
        });
      }
    }
  },
  mounted: function () {
    FieldValidatorAcknowledgeFG.bind(this)();
    if (this.required || this.validate) {
      this.valid = false;
    } else {
      this.valid = true;
    }
  },
  destroyed: function () {
    this.fg.onChildDestroyed(this);
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@100;300;400;500;700&display=swap');

@media (min-width: 280px) {
  /* galaxy fold */
  .ui-input {
    position: relative;
    margin: 32px 0 24px 0;

    width: 100%;
    max-width: 343px;
    height: 70px;

    vertical-align: top;
  }

  .ui-input input {
    width: 100%;
    height: 46px;

    background-color: #ffffff;
    outline: none;

    border: none;
    padding: 10px 24px;
    border-radius: 25px;
    border: 1px solid #969696;

    font-family: "Open Sans";
    font-style: normal;
    font-weight: normal;
    font-size: 15px;
    line-height: 25px;
    color: #4c4c4c;
  }

  .ui-input[disabled] input {
    background-color: #e5e5e5;
  }

  .ui-input[touched]:not([valid]) input {
    border-color: #ed1c24;
  }

  .ui-input[valid].show-valid-indicator input {
    border-color: #1ea89c;
  }

  .ui-input:not(.show-valid-indicator) .valid-icon {
    display: none;
  }

  .ui-input:not([valid]).show-valid-indicator .valid-icon {
    display: none;
  }

  .ui-input[valid].show-valid-indicator .valid-icon {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .ui-input .label {
    position: absolute;
    margin: 0;
    height: 25px;
    left: 24px;
    top: 9.5px;

    font-family: "Open Sans";
    font-style: normal;
    font-weight: normal;
    font-size: 15px;
    line-height: 25px;
    color: #4c4c4c;

    transition: transform 0.1s ease-in-out, font-size 0.1s ease-in-out,
    background-color 0.2s ease, padding 0.2s ease;
  }

  .ui-input .up {
    font-size: 12px !important;
    transform: translateY(-35px);
  }

  .icon-padding {
    padding-right: 46px !important;
  }

  .ui-input .valid-icon {
    display: inline-block;
    position: absolute;
    right: 3px;
    top: 3px;
    width: 40px;
    height: 40px;

    border: 3px solid transparent;
    border-radius: 24px;
    cursor: pointer;

    /* TEMP UNTIL I GET ICONS */
    text-align: center;
    line-height: 34px;
    color: whitesmoke;
    font-weight: 800;
    font-family: "Open Sans";
  }

  /* MINI */
  .mini {
    display: inline-block;
    width: 70px;
    height: 46px !important;
    margin: 10px 8px 10px 0;
  }

  .mini input {
    padding: 10px 12px !important;
    border-radius: 16px;
  }

  .mini span.label {
    left: 12px;
  }

  /* INPUT FIELDS WITH (?) INFO */
  .info {
    width: 90% !important;
    display: inline-block;
  }

  .ui-input .autocomplete-options {
    position: absolute;
    z-index: 3;
    height: 0px;
    overflow: hidden;
    visibility: hidden;

    transition: height 0.2s ease-in-out, border-radius 0.1s ease-in-out, transform 0.1s ease-in-out;
    transform: translateY(-2px);
    background-color: white;

    border: 1px solid #4c4c4c;
    box-shadow: 0px 4px 4px -4px rgba(66, 66, 66, 0.2);
    border-radius: 0 0 23px 23px;
    border-top: none;
  }

  .ui-input .autocomplete-options .autocomplete-options__list {
    height: calc(100% - 22px);
    border-radius: 0 0 23px 23px;
    padding: 0 !important;

    overflow: hidden;
    overflow-y: auto;
  }

  .ui-input .autocomplete-options .autocomplete-options__list .options__list-option {
    padding: 11px 24px;
    background-color: white;

    border: none;
    cursor: pointer;
  }

  .ui-input .autocomplete-options .autocomplete-options__list .options__list-option .option__text {
    font-family: "Open Sans";
    font-style: normal;
    font-weight: normal;
    font-size: 15px;
    line-height: 25px;
    color: #4c4c4c;
  }

  .ui-input .autocomplete-options .google-footer {
    position: absolute;
    bottom: 0;
    right: 0;
    height: 22px;
    width: 100%;

    background-color: white;
  }

  .ui-input .autocomplete-options .google-footer .logo {
    display: block;
    position: absolute;
    right: 12px;
    top: 2px;
    height: 18px;
  }
}

@media (min-width: 320px) {
  /* smartphones, iPhone, portrait 480x320 phones */
}

@media (min-width: 481px) {
  /* portrait e-readers (Nook/Kindle), smaller tablets @ 600 or @ 640 wide. */
}

@media (min-width: 641px) {
  /* portrait tablets, portrait iPad, landscape e-readers, landscape 800x480 or 854x480 phones */
}

@media (min-width: 961px) {
  /* tablet, landscape iPad, lo-res laptops and desktops */
}

@media (min-width: 1024px) {
  /* big landscape tablets, laptops, and desktops */
}

@media (min-width: 1281px) {
  /* hi-res laptops and desktops */
}
</style>
